import { useEffect, useRef, useState } from "react";
import { useVideoContext } from "./VideoProvider";
import { VolumeMuteIcon, VolumeUpIcon } from "../../assets";
import "react-loading-skeleton/dist/skeleton.css";
import Skeleton from "react-loading-skeleton";

const VideoPlayer = ({
  src,
  autoPlay = false,
  continuousPlay = false,
  skeletonStyle = {},
  skeletonHeight = null,
  skeletonWidth = null,
  ...rest
}) => {
  const [loaded, setLoaded] = useState(false);
  const videoRef = useRef(null);
  const { muted, playVideo, toggleMute, registerVideo, unregisterVideo } =
    useVideoContext();
  const [isPlaying, setIsPlaying] = useState(false);
  const [inView, setInView] = useState(false);
  const wasPlayingRef = useRef(false); // track state before tab lost focus

  // Register video with context
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = muted;
      videoRef.current._setIsPlaying = setIsPlaying;
      registerVideo(videoRef.current);

      videoRef.current.loop = continuousPlay;
    }
    return () => unregisterVideo(videoRef.current);
  }, [muted, continuousPlay, registerVideo, unregisterVideo]);

  // Detect if video is in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.6 }
    );
    if (videoRef.current) observer.observe(videoRef.current);
    return () => {
      if (videoRef.current) observer.unobserve(videoRef.current);
    };
  }, []);

  // Auto play/pause when in view & tab visible
  useEffect(() => {
    if (!videoRef.current) return;

    if (inView && autoPlay && !document.hidden) {
      playVideo(videoRef.current);
      setIsPlaying(true);
    } else if (!inView || document.hidden) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  }, [inView, autoPlay, playVideo]);

  // Handle tab visibility
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!videoRef.current) return;

      if (document.hidden) {
        // Save if it was playing before
        wasPlayingRef.current = !videoRef.current.paused;
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        // Resume only if it was playing before blur
        if (wasPlayingRef.current) {
          playVideo(videoRef.current);
          setIsPlaying(true);
        }
        wasPlayingRef.current = false;
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [playVideo]);

  const handleTogglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      playVideo(videoRef.current);
      setIsPlaying(true);
    }
  };

  const renderSkeleton = () => {
    const hasDimensions = skeletonHeight || skeletonWidth;

    const skeletonEl = (
      <Skeleton
        {...(skeletonHeight && { height: skeletonHeight })}
        {...(skeletonWidth && { width: skeletonWidth })}
        style={skeletonStyle}
      />
    );

    return hasDimensions ? (
      skeletonEl
    ) : (
      <div style={skeletonStyle}>{skeletonEl}</div>
    );
  };

  return (
    <>
      {!loaded && renderSkeleton()}

      <div
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          display: loaded ? "flex" : "none",
          opacity: loaded ? 1 : 0,
          alignItems: "center",
          transition: "opacity 0.3s ease",
          justifyContent: "center",
        }}
      >
        {/* Centered video */}
        <video
          ref={videoRef}
          src={src}
          crossOrigin="anonymous"
          playsInline
          {...rest}
          onClick={handleTogglePlay}
          onLoadedData={() => setLoaded(true)}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          style={{
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
          }}
        />

        {/* Sound control */}
        <button
          onClick={toggleMute}
          style={{
            position: "absolute",
            bottom: "8px",
            right: "8px",
            background: "rgba(0,0,0,0.6)",
            border: "none",
            color: "white",
            borderRadius: "50%",
            width: "32px",
            height: "32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          {muted ? <VolumeMuteIcon size={18} /> : <VolumeUpIcon size={18} />}
        </button>

        {/* Overlay play icon */}
        {!isPlaying && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              fontSize: "48px",
              color: "white",
              opacity: 0.8,
              pointerEvents: "none",
            }}
          >
            ▶
          </div>
        )}
      </div>
    </>
  );
};

export default VideoPlayer;
